import React, { useMemo } from 'react';
import { Lead } from '../../services/leadService';
import {
    ActiveProjectPoint,
    LEAD_BOARD_COLUMNS,
    LEAD_STATUS_LABELS,
    LeadStatus,
    allowedTransitions,
    computeLeadPriority,
} from '../../services/leadLogic';
import { brancheLabel } from '../../services/branchen';
import { MapPin, Sparkles } from 'lucide-react';

/**
 * Pipeline als Kanban. Duenne Darstellung — Spalten, Reihenfolge und erlaubte
 * Statuswechsel kommen vollstaendig aus leadLogic.ts.
 *
 * KEIN Drag & Drop: der Statuswechsel laeuft ueber ein Auswahlfeld je Karte.
 * Das ist bewusst so. Ein Drag-Ziel, das der Uebergang gar nicht zulaesst,
 * muesste die Karte zurueckschnellen lassen — ein Auswahlfeld zeigt von
 * vornherein nur die zulaessigen Ziele. Ausserdem ist es mit Tastatur bedienbar
 * (ERP-Plan 11, „Accessibility": Kanban braucht eine Tastatur-Alternative).
 */
interface Props {
    leads: Lead[];
    /** Bezugspunkte der Naehe-Bewertung (leadService.fetchActiveProjectPoints). */
    activeProjects: ActiveProjectPoint[];
    onSelect: (lead: Lead) => void;
    /** Der Aufrufer entscheidet ueber Pflichtfelder (Grund bei „verloren"). */
    onStatusChange: (lead: Lead, to: LeadStatus) => void;
}

/** Farbverlauf der Pipeline (Plan 6: „neu=blau … angebot=amber"). */
const SPALTEN_FARBE: Record<LeadStatus, string> = {
    neu: 'border-t-blue-400 bg-blue-50/40',
    qualifiziert: 'border-t-sky-400 bg-sky-50/40',
    kontaktiert: 'border-t-teal-400 bg-teal-50/40',
    angebot: 'border-t-amber-400 bg-amber-50/40',
    gewonnen: 'border-t-emerald-500 bg-emerald-50/40',
    verloren: 'border-t-slate-300 bg-slate-50',
    gesperrt: 'border-t-red-400 bg-red-50/40',
};

/** Alter in Tagen — die Zahl, an der ein liegengebliebener Lead auffaellt. */
const alterInTagen = (iso?: string): number | null => {
    if (!iso) return null;
    const t = Date.parse(iso);
    if (!Number.isFinite(t)) return null;
    return Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
};

const alterText = (tage: number | null): string => {
    if (tage === null) return '';
    if (tage === 0) return 'heute';
    if (tage === 1) return 'seit 1 Tag';
    return `seit ${tage} Tagen`;
};

/** Ab hier ist ein Lead auffaellig alt und wird optisch markiert. */
const ALT_AB_TAGEN = 30;

const scoreFarbe = (score: number): string => {
    if (score >= 75) return 'bg-emerald-100 text-emerald-700';
    if (score >= 50) return 'bg-amber-100 text-amber-700';
    return 'bg-slate-100 text-slate-500';
};

export const LeadsBoard: React.FC<Props> = ({ leads, activeProjects, onSelect, onStatusChange }) => {
    /**
     * Je Spalte nach Prioritaet sortiert (Score + Naehe-Bonus): oben steht,
     * was als naechstes anzurufen ist. Die Rechnung liegt in leadLogic und
     * wird hier nur angewandt.
     */
    const spalten = useMemo(() => {
        const map = new Map<LeadStatus, { lead: Lead; prio: ReturnType<typeof computeLeadPriority> }[]>();
        for (const status of LEAD_BOARD_COLUMNS) map.set(status, []);
        for (const lead of leads) {
            const eintrag = { lead, prio: computeLeadPriority(lead, activeProjects) };
            map.get(lead.status)?.push(eintrag);
        }
        for (const liste of map.values()) liste.sort((a, b) => b.prio.priority - a.prio.priority);
        return map;
    }, [leads, activeProjects]);

    return (
        <div className="overflow-x-auto pb-2">
            <div className="flex gap-4 min-w-max">
                {LEAD_BOARD_COLUMNS.map((status) => {
                    const eintraege = spalten.get(status) ?? [];
                    return (
                        <section
                            key={status}
                            aria-label={`Spalte ${LEAD_STATUS_LABELS[status]}`}
                            data-glass="spalte"
                            className={`w-[280px] shrink-0 rounded-2xl border border-slate-200 border-t-4 ${SPALTEN_FARBE[status]}`}
                        >
                            <header className="px-4 py-3 flex items-center justify-between">
                                <h3 className="font-bold text-sm text-slate-700">{LEAD_STATUS_LABELS[status]}</h3>
                                <span className="text-[11px] font-bold text-slate-500 bg-white/70 rounded-full px-2 py-0.5">
                                    {eintraege.length}
                                </span>
                            </header>

                            <div className="px-3 pb-3 space-y-2 max-h-[calc(100vh-320px)] overflow-y-auto">
                                {eintraege.length === 0 && (
                                    <p className="text-[11px] text-slate-400 px-1 py-3">Keine Leads.</p>
                                )}

                                {eintraege.map(({ lead, prio }) => {
                                    const tage = alterInTagen(lead.createdAt);
                                    const ziele = allowedTransitions(lead.status, { customerId: lead.customerId });
                                    return (
                                        <article
                                            key={lead.id}
                                            className="bg-white rounded-xl border border-slate-200 shadow-sm hover:border-blue-300 transition-colors"
                                        >
                                            <button
                                                type="button"
                                                onClick={() => onSelect(lead)}
                                                className="w-full text-left p-3 space-y-1.5"
                                            >
                                                <div className="flex items-start justify-between gap-2">
                                                    <span className="font-bold text-sm text-slate-800 leading-tight">
                                                        {lead.name || 'Unbenannt'}
                                                    </span>
                                                    <span
                                                        title={prio.begruendung}
                                                        className={`shrink-0 text-[11px] font-bold rounded-full px-2 py-0.5 ${scoreFarbe(prio.priority)}`}
                                                    >
                                                        {lead.score == null ? '—' : prio.priority}
                                                    </span>
                                                </div>

                                                <p className="text-[11px] text-slate-500">{brancheLabel(lead.branche)}</p>

                                                {(lead.city || lead.zip) && (
                                                    <p className="text-[11px] text-slate-500 flex items-center gap-1">
                                                        <MapPin className="w-3 h-3 text-slate-300 shrink-0" />
                                                        {[lead.zip, lead.city].filter(Boolean).join(' ')}
                                                    </p>
                                                )}

                                                <div className="flex items-center justify-between pt-0.5">
                                                    <span
                                                        className={`text-[11px] ${tage !== null && tage >= ALT_AB_TAGEN ? 'text-amber-600 font-bold' : 'text-slate-400'}`}
                                                        title={tage !== null && tage >= ALT_AB_TAGEN ? 'Liegt ungewöhnlich lange in dieser Stufe' : undefined}
                                                    >
                                                        {alterText(tage)}
                                                    </span>
                                                    {prio.naeheBonus > 0 && (
                                                        <span
                                                            className="text-[11px] text-emerald-600 flex items-center gap-0.5"
                                                            title={prio.begruendung}
                                                        >
                                                            <Sparkles className="w-3 h-3" />
                                                            {prio.distanceKm !== null ? `${Math.round(prio.distanceKm)} km` : ''}
                                                        </span>
                                                    )}
                                                </div>
                                            </button>

                                            {ziele.length > 0 && (
                                                <div className="px-3 pb-3">
                                                    <label className="sr-only" htmlFor={`status-${lead.id}`}>
                                                        Status von {lead.name} ändern
                                                    </label>
                                                    <select
                                                        id={`status-${lead.id}`}
                                                        value=""
                                                        onChange={(e) => {
                                                            const to = e.target.value as LeadStatus;
                                                            e.currentTarget.value = '';
                                                            if (to) onStatusChange(lead, to);
                                                        }}
                                                        className="w-full text-[11px] border border-slate-200 rounded-lg px-2 py-1.5 bg-slate-50 text-slate-600 outline-none focus:ring-2 focus:ring-blue-500/20"
                                                    >
                                                        <option value="">Verschieben nach…</option>
                                                        {ziele.map((z) => (
                                                            <option key={z} value={z}>{LEAD_STATUS_LABELS[z]}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}
                                        </article>
                                    );
                                })}
                            </div>
                        </section>
                    );
                })}
            </div>
        </div>
    );
};
